import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import { ScrollArea } from "../components/ui/scroll-area";
import {
  Leaf, BookOpen, Plus, ArrowLeft, Calendar, Cloud, Loader2
} from "lucide-react";
import { format } from "date-fns";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const JournalPage = () => {
  const { user } = useAuth();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newEntry, setNewEntry] = useState({
    garden_plan_id: "",
    title: "",
    content: "",
    weather: "",
    plants_mentioned: []
  });

  useEffect(() => {
    fetchEntries();
  }, []);

  const fetchEntries = async () => {
    try {
      const response = await axios.get(`${API}/journal`, { withCredentials: true });
      setEntries(response.data);
    } catch (error) {
      console.error("Failed to fetch entries:", error);
      toast.error("Failed to load journal entries");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateEntry = async () => {
    if (!newEntry.title || !newEntry.content) {
      toast.error("Please fill in title and content");
      return;
    }

    setSaving(true);
    try {
      const response = await axios.post(`${API}/journal`, newEntry, { withCredentials: true });
      setEntries([response.data, ...entries]);
      setDialogOpen(false);
      setNewEntry({ garden_plan_id: "", title: "", content: "", weather: "", plants_mentioned: [] });
      toast.success("Journal entry created!");
    } catch (error) {
      console.error("Failed to create entry:", error);
      toast.error("Failed to create entry");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-40 glass border-b border-border/50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/saved" className="text-muted-foreground hover:text-primary">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div>
              <h1 className="font-serif text-xl font-bold">Garden Journal</h1>
              <p className="text-sm text-muted-foreground">{entries.length} entries</p>
            </div>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="btn-primary" data-testid="new-entry-btn">
                <Plus className="w-4 h-4 mr-2" />
                New Entry
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-lg">
              <DialogHeader>
                <DialogTitle className="font-serif text-xl">New Journal Entry</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div>
                  <Label htmlFor="title">Title</Label>
                  <Input
                    id="title"
                    placeholder="Today's garden observations..."
                    value={newEntry.title}
                    onChange={(e) => setNewEntry({ ...newEntry, title: e.target.value })}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="content">Notes</Label>
                  <Textarea
                    id="content"
                    placeholder="What did you notice in your garden today?"
                    value={newEntry.content}
                    onChange={(e) => setNewEntry({ ...newEntry, content: e.target.value })}
                    className="mt-1 min-h-32"
                  />
                </div>
                <div>
                  <Label htmlFor="weather">Weather (optional)</Label>
                  <Input
                    id="weather"
                    placeholder="Sunny, 72°F"
                    value={newEntry.weather}
                    onChange={(e) => setNewEntry({ ...newEntry, weather: e.target.value })}
                    className="mt-1"
                  />
                </div>
                <Button 
                  onClick={handleCreateEntry} 
                  className="w-full btn-primary"
                  disabled={saving}
                >
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  Save Entry
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8">
        {loading ? (
          <div className="flex justify-center py-20">
            <div className="spinner" />
          </div>
        ) : entries.length === 0 ? (
          <div className="text-center py-20">
            <BookOpen className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-50" />
            <h2 className="font-serif text-2xl font-semibold mb-2">No entries yet</h2>
            <p className="text-muted-foreground mb-6">Start documenting your garden journey</p>
          </div>
        ) : (
          <div className="space-y-6">
            {entries.map((entry, index) => (
              <motion.div
                key={entry.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <Card className="card-organic">
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <CardTitle className="font-serif text-xl">{entry.title}</CardTitle>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Calendar className="w-4 h-4" />
                        {format(new Date(entry.created_at), "MMM d, yyyy")}
                      </div>
                    </div>
                    {entry.weather && (
                      <div className="flex items-center gap-1 text-sm text-muted-foreground">
                        <Cloud className="w-4 h-4" />
                        {entry.weather}
                      </div>
                    )}
                  </CardHeader>
                  <CardContent>
                    <p className="text-foreground whitespace-pre-wrap">{entry.content}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default JournalPage;
